// ShoogieGolf Theme - Global JavaScript
// Golf-themed ecommerce functionality

(function() {
  'use strict';

  // Theme initialization
  window.ShoogieGolf = window.ShoogieGolf || {};

  // Cart functionality
  ShoogieGolf.cart = {
    addItem: async function(variantId, quantity = 1, triggerElement = null, options = {}) {
      // Support adding extra items (e.g. bundled laces) in the same atomic
      // request. Pass `options.extraItems = [{id, quantity}, ...]` to bundle.
      const extraItems = Array.isArray(options.extraItems) ? options.extraItems : [];
      const properties = options.properties && typeof options.properties === 'object'
        ? options.properties
        : null;

      // Loading state on the button — user feedback that the click registered.
      if (triggerElement) {
        triggerElement.dataset.wasDisabled = triggerElement.disabled ? '1' : '0';
        triggerElement.disabled = true;
        triggerElement.classList.add('is-cart-loading');
      }
      const restoreTriggerState = () => {
        if (!triggerElement) return;
        triggerElement.disabled = triggerElement.dataset.wasDisabled === '1';
        triggerElement.classList.remove('is-cart-loading');
        delete triggerElement.dataset.wasDisabled;
      };

      try {
        const useBundled = extraItems.length > 0;
        const primaryItem = { id: variantId, quantity: quantity };
        if (properties) primaryItem.properties = properties;
        const body = useBundled
          ? { items: [primaryItem, ...extraItems] }
          : primaryItem;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('Too many requests. Please wait a moment and try again.');
          }
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Failed to add to cart');
        }

        const item = await response.json();

        // Add succeeded — restore the button state and fire instant feedback.
        restoreTriggerState();

        if (window.confettiBurst) {
          let x = window.innerWidth / 2;
          let y = window.innerHeight / 2;
          if (triggerElement) {
            const rect = triggerElement.getBoundingClientRect();
            x = rect.left + rect.width / 2;
            y = rect.top + rect.height / 2;
          }
          window.confettiBurst(x, y, 50);
        }

        // No success toast — opening the cart drawer below is the feedback.
        document.dispatchEvent(new CustomEvent('cart:open'));

        // === ONE /cart.js FETCH (was three, sequential) ===
        // Fetch the fresh cart once, share it across all three UI updaters.
        // Previously each updater fetched independently — same data, 3x cost.
        const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
        const cart = await cartResponse.json();

        // Update count, drawer contents, and free-shipping bar from the
        // shared cart object. These run sequentially but are all sync DOM
        // work after this point — no more network in the critical path.
        this.updateCartCount(cart);
        this.refreshCartDrawer(cart);
        this.updateFreeShippingProgress(true, cart);

        return item;
      } catch (error) {
        restoreTriggerState();
        console.error('Error adding item to cart:', error);
        if (window.showToast) {
          window.showToast(error.message || 'Error adding item to cart', 'error', 3000);
        } else {
          this.showNotification('Error adding item to cart', 'error');
        }
        throw error;
      }
    },

    updateCartCount: async function(prefetchedCart) {
      try {
        // Use the cart that was already fetched if provided; otherwise fetch.
        const cart = prefetchedCart || await (await fetch('/cart.js', { cache: 'no-store' })).json();
        const count = this.getDisplayItemCount(cart);

        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = count;
          el.style.display = count > 0 ? 'block' : 'none';
        });

        return cart;
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    },

    refreshCartDrawer: async function(prefetchedCart) {
      try {
        const cart = prefetchedCart || await (await fetch('/cart.js', { cache: 'no-store' })).json();
        ShoogieGolf.cart.currentCart = cart;

        // Update cart drawer content
        const drawer = document.getElementById('cart-drawer');
        const cartItemsContainer = document.querySelector('[data-cart-items]');
        const cartSubtotal = document.querySelector('[data-cart-subtotal]');

        if (!cartItemsContainer) return;

        if (drawer) {
          drawer.classList.toggle('is-empty', cart.items.length === 0);
        }

        if (cart.items.length === 0) {
          cartItemsContainer.innerHTML = ShoogieGolf.cart.emptyCartMarkup();
          if (cartSubtotal) cartSubtotal.textContent = ShoogieGolf.formatMoney(0);
          return;
        }

        // Render cart items. Component pilot lines are grouped back into the
        // customer-facing kit they came from while staying separate inventory
        // lines inside Shopify.
        let itemsHtml = '';
        this.getCartDisplayEntries(cart).forEach(entry => {
          itemsHtml += entry.type === 'kit'
            ? this.renderKitCartItem(entry)
            : this.renderCartItem(entry.item);
        });

        cartItemsContainer.innerHTML = itemsHtml;

        // Update subtotal
        if (cartSubtotal) {
          cartSubtotal.textContent = ShoogieGolf.formatMoney(cart.total_price);
        }

        // Update free shipping progress (reuse the cart we already have)
        await this.updateFreeShippingProgress(false, cart);
      } catch (error) {
        console.error('Error refreshing cart drawer:', error);
      }
    },

    escapeHtml: function(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      // Quotes too: the result goes into attributes, and titles can come from line-item properties.
      return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },

    getDisplayItemCount: function(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;
      return this.getCartDisplayEntries(cart).reduce((total, entry) => {
        if (entry.type === 'kit') {
          if (entry.kind === 'builder-build') return total + 1;
          const shellItem = entry.items.find(item => (item.properties || {})._kit_role === 'shell') || entry.items[0];
          return total + (shellItem ? shellItem.quantity : 0);
        }

        return total + entry.item.quantity;
      }, 0);
    },

    cartRemoveIcon: function(size = 18) {
      return `
        <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      `;
    },

    getCartDisplayEntries: function(cart) {
      const entries = [];
      const kitEntries = new Map();

      cart.items.forEach(item => {
        const properties = item.properties || {};
        const kitId = properties._kit_id;

        if (!kitId) {
          entries.push({ type: 'item', item });
          return;
        }

        if (!kitEntries.has(kitId)) {
          const kitEntry = {
            type: 'kit',
            kitId,
            title: properties._kit_title || item.product_title,
            size: properties._kit_size || '',
            kind: properties._kit_kind || '',
            items: []
          };
          kitEntries.set(kitId, kitEntry);
          entries.push(kitEntry);
        }

        kitEntries.get(kitId).items.push(item);
      });

      return entries;
    },

    renderCartItem: function(item) {
      const title = this.escapeHtml(item.product_title);
      const variantTitle = item.variant_title ? this.escapeHtml(item.variant_title) : '';
      const image = item.image ? this.escapeHtml(item.image) : '';

      return `
        <div class="cart-item" data-line-item="${this.escapeHtml(item.key)}">
          <div class="cart-item__image">
            ${image ? `<img src="${image}" alt="${title}" width="80" height="80" loading="lazy">` : ''}
          </div>
          <div class="cart-item__header">
            <div class="cart-item__info">
              <h3 class="cart-item__title">${title}</h3>
              ${variantTitle ? `<p class="cart-item__variant">${variantTitle}</p>` : ''}
              ${item.original_line_price && item.original_line_price > item.final_line_price && item.final_line_price > 0 ? `<p class="cart-item__savings">Save ${ShoogieGolf.formatMoney(item.original_line_price - item.final_line_price)}</p>` : ''}
            </div>
            <div class="cart-item__price">${ShoogieGolf.formatMoney(item.final_line_price)}</div>
          </div>
          <div class="cart-item__bottom">
            <div class="cart-item__actions">
              <div class="cart-item__quantity">
                <button class="qty-btn" data-cart-update="${this.escapeHtml(item.key)}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">-</button>
                <span class="qty-value">${item.quantity}</span>
                <button class="qty-btn" data-cart-update="${this.escapeHtml(item.key)}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-item__remove" data-cart-remove="${this.escapeHtml(item.key)}" aria-label="Remove item">
                ${this.cartRemoveIcon()}
              </button>
            </div>
          </div>
        </div>
      `;
    },

    renderKitCartItem: function(entry) {
      if (entry.kind === 'builder-build') return this.renderBuilderKitCartItem(entry);

      const shellItem = entry.items.find(item => (item.properties || {})._kit_role === 'shell') || entry.items[0];
      const shellItems = entry.items.filter(item => (item.properties || {})._kit_role === 'shell');
      const hasExtraShells = shellItems.length > 1 || entry.items.some(item => (item.properties || {})._kit_extra_shell === 'true');
      // A kit with more than one component shows the component breakdown. With an
      // insert it's a full-look "Bundle" (neon); shell + laces only is "With Laces" (sky).
      const isBundle = hasExtraShells || entry.items.length > 1;
      const hasInsert = entry.items.some(item => (item.properties || {})._kit_role === 'insert');
      const finalLinePrice = entry.items.reduce((total, item) => total + item.final_line_price, 0);
      const originalLinePrice = entry.items.reduce((total, item) => total + (item.original_line_price || item.final_line_price), 0);
      // "Save $X" should reflect real markdowns only. Exclude fully-free/"included"
      // lines (e.g. laces free with a pair) so their price isn't shown as a saving.
      const paidItems = entry.items.filter(item => item.final_line_price > 0);
      const paidFinal = paidItems.reduce((total, item) => total + item.final_line_price, 0);
      const paidOriginal = paidItems.reduce((total, item) => total + (item.original_line_price || item.final_line_price), 0);
      const quantity = shellItem ? shellItem.quantity : entry.items[0].quantity;
      const title = this.escapeHtml(entry.title);
      const size = entry.size ? this.escapeHtml(entry.size) : '';
      const imageSource = shellItem?.image || shellItem?.properties?._kit_image || entry.items[0]?.properties?._kit_image || '';
      const image = imageSource ? this.escapeHtml(imageSource) : '';
      const kitId = this.escapeHtml(entry.kitId);
      const summary = this.kitContentsSummary(entry);
      const expanded = !!(this.expandedKits && this.expandedKits.has(entry.kitId));

      return `
        <div class="cart-item cart-item--kit" data-kit-id="${kitId}">
          ${image ? `<div class="cart-item__image"><img src="${image}" alt="${title}" width="80" height="80" loading="lazy"></div>` : ''}
          <div class="cart-item__header">
            <div class="cart-item__info">
              ${isBundle ? `<span class="cart-item__bundle-tag${hasInsert ? '' : ' cart-item__bundle-tag--laces'}"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="m2 17 10 5 10-5M2 12l10 5 10-5"/></svg>${hasInsert ? 'Bundle' : 'With Laces'}</span>` : ''}
              <h3 class="cart-item__title">${title}</h3>
              ${size ? `<p class="cart-item__variant">${size}</p>` : ''}
              ${isBundle ? `<p class="cart-item__contents">${this.escapeHtml(summary)}</p>` : ''}
              ${paidOriginal > paidFinal ? `<p class="cart-item__savings">Save ${ShoogieGolf.formatMoney(paidOriginal - paidFinal)}</p>` : ''}
            </div>
            <div class="cart-item__headside">
              <button class="cart-item__remove" data-cart-remove-kit="${kitId}" aria-label="Remove ${title}">
                ${this.cartRemoveIcon()}
              </button>
              <div class="cart-item__price">${ShoogieGolf.formatMoney(finalLinePrice)}</div>
            </div>
          </div>
          ${isBundle ? `
            <button type="button" class="cart-kit-toggle" data-kit-toggle="${kitId}" aria-expanded="${expanded}" aria-controls="kit-details-${kitId}">
              <span class="cart-kit-toggle__label">What's included</span>
              <svg class="cart-kit-toggle__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div class="cart-kit-details${expanded ? ' is-open' : ''}" id="kit-details-${kitId}">
              ${this.renderKitGroups(entry)}
            </div>
          ` : ''}
        </div>
      `;
    },

    // Plain-English "what's inside" line for a bundle, in the customer's terms
    // (looks/pairs), not components. Laces/insert read as "included" since they
    // don't carry a separate charge.
    kitContentsSummary: function(entry) {
      const props = (i) => i.properties || {};
      const items = entry.items || [];
      const shellCount = items
        .filter(i => props(i)._kit_role === 'shell')
        .reduce((n, i) => n + (i.quantity || 1), 0);
      const pairs = shellCount || 1;
      const hasLaces = items.some(i => props(i)._kit_role === 'laces');
      const hasInsert = items.some(i => props(i)._kit_role === 'insert');
      const lead = `${pairs} ${pairs === 1 ? 'pair' : 'pairs'}`;
      const included = [];
      if (hasLaces) included.push('laces');
      if (hasInsert) included.push('insert');
      return included.length ? `${lead} · ${included.join(' + ')} included` : lead;
    },

    renderKitGroups: function(entry) {
      const self = this;
      const props = (i) => i.properties || {};
      const items = entry.items || [];
      const inserts = items.filter(i => props(i)._kit_role === 'insert');
      const shells = items.filter(i => props(i)._kit_role === 'shell');
      const laces = items.filter(i => props(i)._kit_role === 'laces'); // consumed as paired to shells

      const thumbHtml = (item) => item.image
        ? `<img class="cart-kit-line__thumb" src="${self.escapeHtml(item.image)}" alt="" width="46" height="46" loading="lazy">`
        : '<span class="cart-kit-line__thumb cart-kit-line__thumb--empty" aria-hidden="true"></span>';
      const priceHtml = (item) => item.final_line_price > 0 ? ShoogieGolf.formatMoney(item.final_line_price) : 'Included';
      const lineHtml = (item, title, meta) => `
        <li>
          ${thumbHtml(item)}
          <span class="cart-kit-line__text">
            <span class="cart-kit-line__title">${self.escapeHtml(title)}</span>
            ${meta ? `<span class="cart-kit-line__meta">${self.escapeHtml(meta)}</span>` : ''}
          </span>
          <strong>${priceHtml(item)}</strong>
        </li>`;

      // Each shell line folds in its chosen lace (paired via _kit_shell_title,
      // consumed in order so duplicate shell colours pair to the right lace).
      const shellLines = shells.map(shell => {
        const title = props(shell)._kit_role_title || shell.product_title || 'Shell';
        const parts = [];
        const li = laces.findIndex(l => props(l)._kit_shell_title === title);
        if (li !== -1) {
          const lace = laces.splice(li, 1)[0];
          const color = lace.variant_title || (props(lace)._kit_role_title || '').replace(/\s*laces$/i, '').trim();
          if (color) parts.push(`${color} laces`);
        }
        if (shell.quantity > 1) parts.push(`Qty ${shell.quantity}`);
        return lineHtml(shell, title, parts.join(' · '));
      }).join('');

      // Any laces that didn't match a shell (safety net) render on their own.
      const orphanLaceLines = laces.map(lace => {
        const color = lace.variant_title || (props(lace)._kit_role_title || 'Laces');
        return lineHtml(lace, `${color} laces`, lace.quantity > 1 ? `Qty ${lace.quantity}` : '');
      }).join('');

      const insertLines = inserts.map(ins =>
        lineHtml(ins, props(ins)._kit_role_title || ins.product_title || 'Insert', 'One per bag · fits every shell')
      ).join('');

      const group = (label, inner) => inner
        ? `<div class="cart-kit-group"><div class="cart-kit-group__header"><h4>${self.escapeHtml(label)}</h4></div><ul>${inner}</ul></div>`
        : '';

      return `
        <div class="cart-kit-groups">
          ${group('Shoes', shellLines + orphanLaceLines)}
          ${group('Insert', insertLines)}
        </div>
      `;
    },

    renderBuilderKitCartItem: function(entry) {
      const finalLinePrice = entry.items.reduce((total, item) => total + item.final_line_price, 0);
      const originalLinePrice = entry.items.reduce((total, item) => total + (item.original_line_price || item.final_line_price), 0);
      // "Save $X" should reflect real markdowns only. Exclude fully-free/"included"
      // lines (e.g. laces free with a pair) so their price isn't shown as a saving.
      const paidItems = entry.items.filter(item => item.final_line_price > 0);
      const paidFinal = paidItems.reduce((total, item) => total + item.final_line_price, 0);
      const paidOriginal = paidItems.reduce((total, item) => total + (item.original_line_price || item.final_line_price), 0);
      const shellItem = entry.items.find(item => (item.properties || {})._kit_role === 'shell') || entry.items[0];
      const imageSource = shellItem?.image || shellItem?.properties?._kit_image || entry.items[0]?.properties?._kit_image || '';
      const image = imageSource ? this.escapeHtml(imageSource) : '';
      const title = this.escapeHtml(entry.title || 'Build Your Pair');
      const size = entry.size ? this.escapeHtml(entry.size) : '';
      const kitId = this.escapeHtml(entry.kitId);
      const summary = this.kitContentsSummary(entry);
      const expanded = !!(this.expandedKits && this.expandedKits.has(entry.kitId));
      return `
        <div class="cart-item cart-item--kit cart-item--builder-kit" data-kit-id="${kitId}">
          <div class="cart-item__image">
            ${image ? `<img src="${image}" alt="${title}" width="80" height="80" loading="lazy">` : ''}
          </div>
          <div class="cart-item__header">
            <div class="cart-item__info">
              <h3 class="cart-item__title">${title}</h3>
              ${size ? `<p class="cart-item__variant">${size}</p>` : ''}
              <p class="cart-item__contents">${this.escapeHtml(summary)}</p>
              ${paidOriginal > paidFinal ? `<p class="cart-item__savings">Save ${ShoogieGolf.formatMoney(paidOriginal - paidFinal)}</p>` : ''}
            </div>
            <div class="cart-item__headside">
              <button class="cart-item__remove" data-cart-remove-kit="${kitId}" aria-label="Remove ${title}">
                ${this.cartRemoveIcon()}
              </button>
              <div class="cart-item__price">${ShoogieGolf.formatMoney(finalLinePrice)}</div>
            </div>
          </div>
          <button type="button" class="cart-kit-toggle" data-kit-toggle="${kitId}" aria-expanded="${expanded}" aria-controls="kit-details-${kitId}">
            <span class="cart-kit-toggle__label">What's included</span>
            <svg class="cart-kit-toggle__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
          </button>
          <div class="cart-kit-details${expanded ? ' is-open' : ''}" id="kit-details-${kitId}">
            ${this.renderKitGroups(entry)}
          </div>
        </div>
      `;
    },

    emptyCartMarkup: function() {
      return `
        <div class="cart-drawer__empty">
          <span class="cart-drawer__empty-icon" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="20" r="1.5"></circle>
              <circle cx="18" cy="20" r="1.5"></circle>
              <path d="M2.5 3h2l2.4 11.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 7H6"></path>
            </svg>
          </span>
          <h3 class="cart-drawer__empty-title">Your cart is empty</h3>
          <p class="cart-drawer__empty-text">Add a pair of Shoobies to get your bag ready for the next round.</p>
          <a class="cart-drawer__empty-link" href="/collections/all">Shop Shoobies</a>
        </div>
      `;
    },

    updateItem: async function(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update cart');
        }

        await response.json();
        await this.updateCartCount();
        await this.refreshCartDrawer();
        await this.updateFreeShippingProgress(false);

      } catch (error) {
        console.error('Error updating cart item:', error);
        if (window.showToast) {
          window.showToast('Error updating cart', 'error', 3000);
        } else {
          this.showNotification('Error updating cart', 'error');
        }
      }
    },

    updateKitItems: async function(kitId, quantity) {
      try {
        const cart = this.currentCart || await (await fetch('/cart.js')).json();
        const kitItems = cart.items.filter(item => (item.properties || {})._kit_id === kitId);
        if (!kitItems.length) return;

        const updates = {};
        kitItems.forEach(item => {
          updates[item.key] = quantity;
        });

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) {
          throw new Error('Failed to update cart');
        }

        const updatedCart = await response.json();
        this.updateCartCount(updatedCart);
        await this.refreshCartDrawer(updatedCart);
        await this.updateFreeShippingProgress(false, updatedCart);
      } catch (error) {
        console.error('Error updating component kit:', error);
        if (window.showToast) {
          window.showToast('Error updating cart', 'error', 3000);
        } else {
          this.showNotification('Error updating cart', 'error');
        }
      }
    },

    removeItem: async function(key) {
      // OPTIMISTIC UI: animate the line item out IMMEDIATELY so the user gets
      // instant feedback. The /cart/change.js request runs in the background.
      // On failure, refreshCartDrawer rebuilds the drawer from the server
      // (restoring the item) + shows an error toast.
      const lineItem = document.querySelector(`[data-line-item="${key}"]`);
      if (lineItem) {
        const startHeight = lineItem.offsetHeight;
        lineItem.style.maxHeight = startHeight + 'px';
        lineItem.style.overflow = 'hidden';
        lineItem.style.transition = 'max-height 0.3s ease, opacity 0.2s ease, margin 0.3s ease, padding 0.3s ease';
        // Disable the remove button so double-click doesn't fire twice
        const removeBtn = lineItem.querySelector('[data-cart-remove]');
        if (removeBtn) removeBtn.disabled = true;
        requestAnimationFrame(() => {
          lineItem.style.opacity = '0';
          lineItem.style.maxHeight = '0';
          lineItem.style.marginTop = '0';
          lineItem.style.marginBottom = '0';
          lineItem.style.paddingTop = '0';
          lineItem.style.paddingBottom = '0';
        });
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!response.ok) throw new Error('Failed to remove item');

        // Single /cart.js fetch, share the cart with all updaters.
        const cart = await (await fetch('/cart.js')).json();

        // Hard-remove the DOM node now that the server confirmed.
        if (lineItem && lineItem.parentNode) lineItem.parentNode.removeChild(lineItem);

        // Update cart count + subtotal + free shipping bar from shared cart.
        this.updateCartCount(cart);
        const cartSubtotal = document.querySelector('[data-cart-subtotal]');
        if (cartSubtotal) cartSubtotal.textContent = ShoogieGolf.formatMoney(cart.total_price);
        // If cart is now empty, swap in the empty message.
        if (cart.items.length === 0) {
          const drawer = document.getElementById('cart-drawer');
          const cartItemsContainer = document.querySelector('[data-cart-items]');
          if (drawer) drawer.classList.add('is-empty');
          if (cartItemsContainer) {
            cartItemsContainer.innerHTML = ShoogieGolf.cart.emptyCartMarkup();
          }
        }
        this.updateFreeShippingProgress(false, cart);

        // No success toast — the line item visibly disappearing IS the feedback.
      } catch (error) {
        console.error('Error removing cart item:', error);
        // Restore: re-render the drawer from server state so the item comes back.
        this.refreshCartDrawer();
        if (window.showToast) {
          window.showToast('Error removing item', 'error', 3000);
        } else {
          this.showNotification('Error removing item', 'error');
        }
      }
    },

    updateFreeShippingProgress: async function(isCartAddition = false, prefetchedCart) {
      // Legacy name kept (many call sites). Now only drives the unified rewards ladder;
      // the old free-shipping progress bar was retired.
      const ladders = document.querySelectorAll('[data-rewards-ladder]');
      if (!ladders.length) return;
      try {
        const cart = prefetchedCart || await (await fetch('/cart.js', { cache: 'no-store' })).json();
        ShoogieGolf.cart.currentCart = cart;
        ShoogieGolf.cart.updateRewardsLadder(cart);
      } catch (error) {
        console.error('Error updating rewards ladder:', error);
      }
    },

    // Unified order-value rewards ladder: free shipping + %-off milestones.
    // Reads each ladder's [data-ladder-config] (amount cents + label) and drives
    // fill / message / marker states from the cart total. The bar is the nudge;
    // real savings come from matching Shopify automatic discounts at checkout.
    // Apply ladder state for a given total (cents) to ONE ladder element.
    // Reused by the cart-driven updater and by the Build-by-Bag builder.
    applyRewardsLadder: function(rl, total) {
      let cfg;
      try {
        cfg = JSON.parse(rl.querySelector('[data-ladder-config]').textContent);
      } catch (e) { return; }
      if (!Array.isArray(cfg) || !cfg.length) return;
      const n = cfg.length;

      // First unreached milestone.
      let i = 0;
      while (i < n && total >= cfg[i].amount) i++;

      // Where each marker sits along the line (0-1), measured from the layout so the fill reaches
      // a pill exactly at its threshold even when the pills are grouped narrower than the line
      // (desktop bottom bar). Falls back to even segments (k+0.5)/n when not laid out yet.
      const fillEl = rl.querySelector('[data-ladder-fill]');
      const markerEls = rl.querySelectorAll('[data-ladder-marker]');
      const lineRect = fillEl && fillEl.parentElement ? fillEl.parentElement.getBoundingClientRect() : null;
      const pos = cfg.map(function(m, k) {
        const mk = markerEls[k];
        if (lineRect && lineRect.width > 0 && mk) {
          const r = mk.getBoundingClientRect();
          if (r.width > 0) return Math.min(Math.max((r.left + r.width / 2 - lineRect.left) / lineRect.width, 0), 1);
        }
        return (k + 0.5) / n;
      });

      // Fill interpolates between marker positions; past the last tier it runs the full line.
      let fill;
      if (i >= n) {
        fill = 1;
      } else {
        const prevAmt = i === 0 ? 0 : cfg[i - 1].amount;
        const curAmt = cfg[i].amount;
        const prevPos = i === 0 ? 0 : pos[i - 1];
        const curPos = pos[i];
        const frac = curAmt > prevAmt ? (total - prevAmt) / (curAmt - prevAmt) : 0;
        fill = Math.min(prevPos + frac * (curPos - prevPos), 1);
      }
      const glowEl = rl.querySelector('[data-ladder-glow]');
      if (fillEl) fillEl.style.transform = 'scaleX(' + fill + ')';
      if (glowEl) glowEl.style.transform = 'scaleX(' + fill + ')';

      const msg = rl.querySelector('[data-ladder-message]');
      if (msg) {
        if (i >= n) {
          msg.classList.add('is-max');
          msg.innerHTML = 'You’ve unlocked <b>' + ShoogieGolf.cart.escapeHtml(cfg[n - 1].label) + '</b>';
        } else {
          msg.classList.remove('is-max');
          msg.innerHTML = 'Add <b>' + ShoogieGolf.formatMoney(cfg[i].amount - total) + '</b> for <b>' + ShoogieGolf.cart.escapeHtml(cfg[i].label) + '</b>';
        }
      }

      // Unlock moment: pop newly reached nodes + the stamp, but only on a change after first paint.
      const unlocked = i - 1;
      const prevUnlocked = rl.dataset.unlocked === undefined ? null : +rl.dataset.unlocked;
      rl.dataset.unlocked = unlocked;
      const pop = function(el) {
        el.classList.remove('is-pop'); void el.offsetWidth; el.classList.add('is-pop');
        setTimeout(function() { el.classList.remove('is-pop'); }, 900);
      };

      // Highest reached % tier gets a subtle "you are here" emphasis (free shipping always stays lit).
      const currentPct = unlocked >= 0 && cfg[unlocked].kind !== 'ship' ? unlocked : -1;
      // The next-tier ring pulses a few times after the total changes, then rests (CSS: 3 runs).
      const prevTotal = rl.dataset.total;
      rl.dataset.total = total;
      const pulseNow = prevTotal === undefined || +prevTotal !== total;

      rl.querySelectorAll('[data-ladder-marker]').forEach(function(mk, k) {
        const reached = total >= cfg[k].amount;
        mk.classList.toggle('is-reached', reached);
        mk.classList.toggle('is-current', k === currentPct);
        mk.classList.toggle('is-next', k === i);
        if (k !== i) mk.classList.remove('is-pulse');
        else if (pulseNow) { mk.classList.remove('is-pulse'); void mk.offsetWidth; mk.classList.add('is-pulse'); }
        if (prevUnlocked !== null && k <= unlocked && k > prevUnlocked) pop(mk);
      });

      // Slim-rail extras (bottom sticky bar only).
      const tip = rl.querySelector('[data-ladder-tip]');
      if (tip) {
        tip.style.left = (fill * 100) + '%';
        tip.style.opacity = total > 0 && i < n ? '1' : '0';
      }
      const stamp = rl.querySelector('[data-ladder-stamp]');
      if (stamp) {
        const ic = stamp.querySelector('[data-ladder-stamp-ic]');
        const txt = stamp.querySelector('[data-ladder-stamp-text]');
        stamp.classList.toggle('is-on', unlocked >= 0);
        if (unlocked < 0) {
          if (ic) ic.style.display = '';
          if (txt) txt.textContent = 'Rewards';
        } else if (cfg[unlocked].kind === 'ship') {
          if (ic) ic.style.display = '';
          if (txt) txt.textContent = String(cfg[unlocked].label).replace(/shipping/i, 'ship'); // keeps the phone row on one line
        } else {
          if (ic) ic.style.display = 'none';
          if (txt) txt.textContent = cfg[unlocked].label;
        }
        if (prevUnlocked !== null && unlocked > prevUnlocked) pop(stamp);
      }
      const peek = rl.querySelector('[data-ladder-peek]');
      if (peek) {
        const after = cfg[i + 1];
        peek.innerHTML = after
          ? 'Then <b>' + ShoogieGolf.cart.escapeHtml(after.label) + '</b> at ' + ShoogieGolf.formatMoney(after.amount).replace(/\.00$/, '')
          : (i >= n ? 'Best price unlocked' : '');
      }
    },

    // Collapsed sticky-bar badge: arc = overall progress, circle = what's already
    // unlocked (grey shipping icon → neon once earned → the % tier), text nudges to next.
    applyRewardsBadge: function(badge, total) {
      const wrap = badge.closest('.rl-wrap');
      let cfg;
      try {
        cfg = JSON.parse((wrap || badge).querySelector('[data-ladder-config]').textContent);
      } catch (e) { return; }
      if (!Array.isArray(cfg) || !cfg.length) return;
      const n = cfg.length;

      let i = 0;
      while (i < n && total >= cfg[i].amount) i++;
      const unlocked = i - 1;      // highest unlocked tier (-1 = none)
      const maxed = i >= n;

      // Ring = progress toward the NEXT tier: empty right after an unlock, full on reaching it.
      let frac;
      if (maxed) {
        frac = 1;
      } else {
        const prevAmt = i === 0 ? 0 : cfg[i - 1].amount;
        const curAmt = cfg[i].amount;
        frac = curAmt > prevAmt ? Math.min(Math.max((total - prevAmt) / (curAmt - prevAmt), 0), 1) : 0;
      }
      const arc = badge.querySelector('[data-badge-arc]');
      if (arc) arc.style.strokeDashoffset = (100.53 * (1 - frac)).toFixed(2); // 2πr, r=16

      // Circle center — what's currently unlocked.
      const inEl = badge.querySelector('[data-badge-in]');
      const truck = badge.querySelector('[data-badge-truck]');
      const pct = badge.querySelector('[data-badge-pct]');
      if (inEl && truck && pct) {
        const pctText = function(m) { return String(m.label).split(' ')[0]; }; // "10% off" -> "10%"
        if (unlocked < 0) {                          // nothing unlocked — grey preview of the first tier
          inEl.classList.remove('is-unlocked');
          if (cfg[0].kind === 'ship') { truck.style.display = ''; pct.style.display = 'none'; }
          else { truck.style.display = 'none'; pct.style.display = ''; pct.textContent = pctText(cfg[0]); }
        } else if (cfg[unlocked].kind === 'ship') {  // free shipping earned — neon truck
          inEl.classList.add('is-unlocked');
          truck.style.display = ''; pct.style.display = 'none';
        } else {                                     // a discount earned — show its %
          inEl.classList.add('is-unlocked');
          truck.style.display = 'none'; pct.style.display = ''; pct.textContent = pctText(cfg[unlocked]);
        }
      }

      // Text — Add $X / for reward  (reward / unlocked at max).
      const a = badge.querySelector('[data-badge-a]');
      const b = badge.querySelector('[data-badge-b]');
      if (a && b) {
        if (maxed) {
          a.innerHTML = '<b>' + ShoogieGolf.cart.escapeHtml(cfg[n - 1].label) + '</b>';
          b.textContent = 'unlocked';
        } else {
          a.innerHTML = 'Add <b>' + ShoogieGolf.formatMoney(cfg[i].amount - total) + '</b>';
          b.textContent = 'for ' + cfg[i].label;
        }
      }
    },

    // The amount the ladder measures. Shopify checks the tier discounts' minimums against
    // the subtotal BEFORE order-level discounts, so use items_subtotal_price (line prices
    // after product discounts like free laces), not total_price (which already has the
    // tier % taken off and would make the bar ask for money the customer already spent).
    rewardsTotal: function(cart) {
      if (!cart) return 0;
      if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
      return typeof cart.total_price === 'number' ? cart.total_price : 0;
    },

    // Cart-driven ladders (cart drawer, PDP). Manual ladders (e.g. the Build-by-Bag
    // builder) carry data-ladder-manual and are driven by their own in-progress total.
    updateRewardsLadder: function(cart) {
      const ladders = document.querySelectorAll('[data-rewards-ladder]:not([data-ladder-manual])');
      const badges = document.querySelectorAll('[data-rewards-badge]');
      if (!ladders.length && !badges.length) return;
      const total = this.rewardsTotal(cart);
      const self = this;
      ladders.forEach(function(rl) { self.applyRewardsLadder(rl, total); });
      badges.forEach(function(bd) { self.applyRewardsBadge(bd, total); });
    },

    showNotification: function(message, type = 'success') {
      const notification = document.createElement('div');
      notification.className = `notification notification-${type}`;
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 80px;
        right: 20px;
        padding: 1rem 1.5rem;
        background: ${type === 'success' ? '#10B981' : '#EF4444'};
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        z-index: 9999;
        animation: slideIn 0.3s ease;
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  };

  // Product variant selector
  ShoogieGolf.variants = {
    init: function() {
      const variantSelectors = document.querySelectorAll('[data-variant-selector]');
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', this.handleVariantChange.bind(this));
      });
    },

    handleVariantChange: function(event) {
      const form = event.target.closest('form');
      const selectedOptions = [];

      form.querySelectorAll('[data-variant-selector]').forEach(selector => {
        selectedOptions.push(selector.value);
      });

      // Find matching variant
      const productData = JSON.parse(form.dataset.product);
      const variant = productData.variants.find(v => {
        return v.options.every((option, index) => option === selectedOptions[index]);
      });

      if (variant) {
        this.updatePrice(variant);
        this.updateAddToCartButton(variant);
        this.updateImages(variant);
      }
    },

    updatePrice: function(variant) {
      const priceContainer = document.querySelector('[data-product-price]');
      if (!priceContainer) return;

      const priceRegular = priceContainer.querySelector('[data-price-regular]');
      const priceCompare = priceContainer.querySelector('[data-price-compare]');
      const priceBadge = priceContainer.querySelector('[data-price-badge]');

      // Update regular price
      if (priceRegular) {
        priceRegular.textContent = ShoogieGolf.formatMoney(variant.price);
      }

      // Update compare-at price and sale badge
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (priceCompare) {
          priceCompare.textContent = ShoogieGolf.formatMoney(variant.compare_at_price);
          priceCompare.hidden = false;
        }
        if (priceBadge) {
          const savings = variant.compare_at_price - variant.price;
          priceBadge.textContent = 'Save ' + ShoogieGolf.formatMoney(savings);
          priceBadge.hidden = false;
        }
      } else {
        if (priceCompare) {
          priceCompare.textContent = '';
          priceCompare.hidden = true;
        }
        if (priceBadge) {
          priceBadge.textContent = '';
          priceBadge.hidden = true;
        }
      }
    },

    updateAddToCartButton: function(variant) {
      const button = document.querySelector('[data-add-to-cart]');
      if (button) {
        if (variant.available) {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        } else {
          button.disabled = true;
          button.textContent = 'Sold Out';
        }
        button.dataset.variantId = variant.id;
      }
    },

    updateImages: function(variant) {
      if (variant.featured_image) {
        const mainImage = document.querySelector('[data-main-image]');
        if (mainImage) {
          mainImage.src = variant.featured_image.src;
        }
      }
    }
  };

// Golf-themed animations
ShoogieGolf.animations = {
  parallaxState: {
    prefersReduced: null,
    elements: [],
    ticking: false,
    listenersBound: false,
    handleScroll: null,
    updateParallax: null
  },

  init: function() {
    this.initGolfBallLoader();
    this.initParallax();
    this.initScrollAnimations();
  },

    initGolfBallLoader: function() {
      // Add golf ball bounce animation to loading states
      const loaders = document.querySelectorAll('.golf-loader');
      loaders.forEach(loader => {
        loader.innerHTML = `
          <div class="golf-ball-bounce" style="
            width: 30px;
            height: 30px;
            background: white;
            border-radius: 50%;
            animation: bounce 1s infinite;
            box-shadow: inset -2px -2px 4px rgba(0,0,0,0.2);
          "></div>
        `;
      });
    },

  initParallax: function() {
    const state = this.parallaxState;

    if (state.prefersReduced === null) {
      state.prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    if (state.prefersReduced) {
      return;
    }

    state.elements = Array.from(document.querySelectorAll('[data-parallax]'));
    if (state.elements.length === 0) {
      return;
    }

    state.updateParallax = () => {
      const viewportHeight = window.innerHeight;

      state.elements.forEach(element => {
        const anchor = element.closest('[data-parallax-anchor]') || element.parentElement || element;
        const rect = anchor.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top > viewportHeight) {
          return;
        }

        const speed = parseFloat(element.dataset.parallax) || 0.5;
        const baseScale = parseFloat(element.dataset.parallaxScale) || 1;
        const bounds = parseFloat(element.dataset.parallaxBounds || 200);

        const offsetToCenter = rect.top + rect.height / 2 - (viewportHeight / 2);
        const translatedRaw = offsetToCenter * speed * -1.25;
        const translated = Math.max(Math.min(translatedRaw, bounds), -bounds);

        const scaleBoost = Math.min(Math.abs(translated) / 280, 0.25);
        const scale = baseScale + scaleBoost;

        element.style.transform = `translate3d(0, ${translated}px, 0) scale(${scale})`;
      });
    };

    state.handleScroll = state.handleScroll || (() => {
      if (state.ticking) return;
      state.ticking = true;
      requestAnimationFrame(() => {
        if (typeof state.updateParallax === 'function') {
          state.updateParallax();
        }
        state.ticking = false;
      });
    });

    if (!state.listenersBound) {
      window.addEventListener('scroll', state.handleScroll, { passive: true });
      window.addEventListener('resize', state.handleScroll);
      state.listenersBound = true;
    }

    state.ticking = false;
    state.handleScroll();
  },

    initScrollAnimations: function() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      document.querySelectorAll('[data-animate]').forEach(el => {
        observer.observe(el);
      });
    }
  };

  // Utility functions
  ShoogieGolf.formatMoney = function(cents) {
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2);
  };

  ShoogieGolf.getUrlParam = function(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };

  // Mobile menu functionality
  ShoogieGolf.mobileMenu = {
    init: function() {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const menu = document.querySelector('[data-mobile-menu]');

      if (toggle && menu) {
        toggle.addEventListener('click', () => {
          menu.classList.toggle('active');
          document.body.classList.toggle('menu-open');
        });
      }
    }
  };

  // Search functionality
  ShoogieGolf.search = {
    init: function() {
      const searchForm = document.querySelector('[data-search-form]');
      if (searchForm) {
        searchForm.addEventListener('submit', this.handleSearch.bind(this));
      }

      // Predictive search
      const searchInput = document.querySelector('[data-search-input]');
      if (searchInput) {
        let debounceTimer;
        searchInput.addEventListener('input', (e) => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.predictiveSearch(e.target.value);
          }, 300);
        });
      }
    },

    handleSearch: function(e) {
      e.preventDefault();
      const query = e.target.querySelector('input').value;
      if (query) {
        window.location.href = `/search?q=${encodeURIComponent(query)}`;
      }
    },

    predictiveSearch: async function(query) {
      if (query.length < 2) return;

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product`);
        const suggestions = await response.json();
        this.displaySuggestions(suggestions);
      } catch (error) {
        console.error('Predictive search error:', error);
      }
    },

    displaySuggestions: function(suggestions) {
      // Implementation for displaying search suggestions
    }
  };

  // Newsletter signup
  ShoogieGolf.newsletter = {
    init: function() {
      const forms = document.querySelectorAll('[data-newsletter-form]');
      forms.forEach(form => {
        form.addEventListener('submit', this.handleSubmit.bind(this));
      });
    },

    handleSubmit: async function(e) {
      e.preventDefault();
      const form = e.target;
      const email = form.querySelector('input[type="email"]').value;

      try {
        // Submit to Shopify customers
        const response = await fetch('/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            'form_type': 'customer',
            'utf8': '✓',
            'contact[email]': email,
            'contact[tags]': 'newsletter'
          })
        });

        if (response.ok) {
          ShoogieGolf.cart.showNotification('Welcome to the Golf Club. Check your email for a special offer.');
          form.reset();
        }
      } catch (error) {
        console.error('Newsletter signup error:', error);
        ShoogieGolf.cart.showNotification('Error signing up. Please try again.', 'error');
      }
    }
  };

  // Initialize theme when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    console.log('ShoogieGolf Theme Initialized');

    // Initialize modules
    ShoogieGolf.variants.init();
    ShoogieGolf.animations.init();
    ShoogieGolf.mobileMenu.init();
    ShoogieGolf.search.init();
    ShoogieGolf.newsletter.init();

    if (window.Shopify && window.Shopify.designMode) {
      ['shopify:section:load', 'shopify:section:select', 'shopify:block:select', 'shopify:block:deselect'].forEach(eventName => {
        document.addEventListener(eventName, () => {
          ShoogieGolf.animations.initParallax();
        });
      });
    }

    // Initialize cart on page load - fetch cart data once and update all components
    async function initializeCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update cart count
        ShoogieGolf.cart.updateCartCount(cart);

        // Update cart drawer if present
        if (document.querySelector('[data-cart-items]')) {
          await ShoogieGolf.cart.refreshCartDrawer();
        }

        // Update cart-aware incentives if present
        if (document.querySelector('[data-rewards-ladder]')) {
          await ShoogieGolf.cart.updateFreeShippingProgress(false, cart);
        }
      } catch (error) {
        console.error('Error initializing cart:', error);
      }
    }

    initializeCart();

    // Product form submission
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.matches('.product-form') || form.querySelector('[data-add-to-cart]')) {
        e.preventDefault();

        const variantIdInput = form.querySelector('[data-variant-id]');
        const quantityInput = form.querySelector('[name="quantity"]');
        const submitButton = form.querySelector('[data-add-to-cart]');

        if (!variantIdInput) return;

        const variantId = variantIdInput.value;
        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

        const lacesInput = document.querySelector('[data-laces-variant-input]');
        const defaultLacesId = lacesInput?.dataset.lacesDefaultVariantId || '';
        // Laces are "included" at the default color (no line); a non-default color adds a paid line.
        const nonDefaultLaceId = (lacesInput && lacesInput.value && lacesInput.value !== defaultLacesId) ? lacesInput.value : '';

        // Complete-pair toggle (shell PDPs): "Complete pair" bundles shell + insert
        // (+ chosen laces) into one _kit_ so the cart drawer groups it like a
        // Build-by-Bag bundle; "Just the shell" is the single-shell reorder path.
        const pairToggle = document.querySelector('[data-pair-toggle]');
        const pairSel = pairToggle ? pairToggle.querySelector('input[data-insert-mode]:checked') : null;
        const pairMode = pairToggle ? ((pairSel && pairSel.dataset.insertMode) || 'pair') : null;

        const extraItems = [];
        let primaryProperties = null;

        if (pairMode === 'pair') {
          const kitId = 'pair-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
          const shellTitle = (document.querySelector('.product-title')?.textContent || 'Shoobie').trim();
          const lookTitle = shellTitle.replace(/\s+(shoe|sandal|feet)\s+pair\s*$/i, '').trim() + ' Full Set';
          const kitImage = document.querySelector('[data-main-image]')?.src || '';
          const shared = {
            _component_kit: 'true',
            _kit_id: kitId,
            _kit_title: lookTitle,
            _kit_image: kitImage,
            _kit_kind: 'pdp-pair'
          };
          primaryProperties = { ...shared, _kit_role: 'shell', _kit_role_title: shellTitle };

          const size = (pairSel && pairSel.dataset.insertSize) || 'Standard';
          const insertId = size === 'Small' ? pairToggle.dataset.smallInsertId : pairToggle.dataset.stdInsertId;
          if (insertId) {
            extraItems.push({ id: insertId, quantity, properties: { ...shared, _kit_role: 'insert', _kit_role_title: size + ' Insert' } });
          }
          // Laces show in the bundle for shoe shells only (the picker exists only for
          // shoes, not sandals/Sasquatch). Laces are included FREE at any colour —
          // the picker uses the $0 "Shoe Laces — Included" product — so add whichever
          // colour the customer picked.
          if (lacesInput && lacesInput.value) {
            extraItems.push({ id: lacesInput.value, quantity, properties: { ...shared, _kit_role: 'laces', _kit_role_title: 'Laces', _kit_shell_title: shellTitle } });
          }
        } else if (pairMode === 'shell' && lacesInput) {
          // "Just the shell" on a shoe shell: laces always come with the shell (free,
          // any colour), so group them ("With Laces") using the picked colour variant.
          const kitId = 'shoe-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
          const shTitle = (document.querySelector('.product-title')?.textContent || 'Shoobie').trim();
          const shColor = shTitle.replace(/\s+(shoe|sandal|feet)\s+pair\s*$/i, '').trim();
          const shShared = { _component_kit: 'true', _kit_id: kitId, _kit_title: shColor, _kit_image: (document.querySelector('[data-main-image]')?.src || ''), _kit_kind: 'shoe-laces' };
          primaryProperties = { ...shShared, _kit_role: 'shell', _kit_role_title: shTitle };
          extraItems.push({ id: lacesInput.value, quantity, properties: { ...shShared, _kit_role: 'laces', _kit_role_title: 'Laces', _kit_shell_title: shTitle } });
        } else if (nonDefaultLaceId) {
          // Non-shell product with a lace add-on: plain extra line.
          extraItems.push({ id: nonDefaultLaceId, quantity });
        }

        if (variantId) {
          ShoogieGolf.cart.addItem(variantId, quantity, submitButton, {
            extraItems,
            properties: primaryProperties
          });
        }
      }
    });

    // Add to cart buttons (for non-form buttons)
    document.addEventListener('click', function(e) {
      // Handle Quick Add buttons
      const quickAddBtn = e.target.closest('.quick-add-btn');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling

        // Prevent double-clicks
        if (quickAddBtn.dataset.adding === 'true') {
          return;
        }

        const variantId = quickAddBtn.dataset.variantId;
        const quantity = parseInt(quickAddBtn.dataset.quantity || 1);
        const pairInsertId = quickAddBtn.dataset.pairInsertId;

        if (variantId) {
          quickAddBtn.dataset.adding = 'true';
          let opts = {};
          if (pairInsertId) {
            // "Full look" quick-add: shell + Standard insert as one _kit_ (same
            // schema as the PDP pair toggle, so the cart drawer groups it).
            const kitId = 'pair-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
            const title = quickAddBtn.dataset.pairTitle || 'Shoobie';
            const lookTitle = title.replace(/\s+(shoe|sandal|feet)\s+pair\s*$/i, '').trim() + ' Full Set';
            const shared = {
              _component_kit: 'true',
              _kit_id: kitId,
              _kit_title: lookTitle,
              _kit_image: quickAddBtn.dataset.pairImage || '',
              _kit_kind: 'pdp-pair'
            };
            const kitExtras = [{ id: pairInsertId, quantity, properties: { ...shared, _kit_role: 'insert', _kit_role_title: 'Standard Insert' } }];
            // Included laces (shoe shells only; data-add-laces is set in Liquid, absent for sandals/Sasquatch).
            if (quickAddBtn.dataset.addLaces === 'true') {
              // Single laces product (shoobie-laces-1): the card's included colour
              // (custom.included_laces) or White. Free with a pair via the
              // "Free laces with every pair" automatic discount.
              const laceId = quickAddBtn.dataset.laceVariantId || '47038373003318';
              const laceTitle = (quickAddBtn.dataset.laceColor || 'White') + ' Laces';
              kitExtras.push({ id: laceId, quantity, properties: { ...shared, _kit_role: 'laces', _kit_role_title: laceTitle } });
            }
            opts = {
              properties: { ...shared, _kit_role: 'shell', _kit_role_title: title },
              extraItems: kitExtras
            };
          }
          ShoogieGolf.cart.addItem(variantId, quantity, quickAddBtn, opts).finally(() => {
            delete quickAddBtn.dataset.adding;
          });
        } else {
          console.error('No variant ID found on Quick Add button!');
        }
        return;
      }

      // Handle other add-to-cart buttons
      const button = e.target.closest('[data-add-to-cart]');
      if (button && !button.closest('form.product-form')) {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        const quantity = parseInt(button.dataset.quantity || 1);
        if (variantId) {
          ShoogieGolf.cart.addItem(variantId, quantity, button);
        }
      }

      // Cart drawer quantity update
      if (e.target.matches('[data-cart-update]')) {
        e.preventDefault();
        const key = e.target.dataset.cartUpdate;
        const quantity = parseInt(e.target.dataset.qty);
        if (quantity >= 0) {
          ShoogieGolf.cart.updateItem(key, quantity);
        }
      }

      // Cart drawer bundle: expand/collapse the "What's included" breakdown.
      // Expanded kit ids persist in a Set so the state survives cart re-renders.
      const kitToggle = e.target.closest('[data-kit-toggle]');
      if (kitToggle) {
        e.preventDefault();
        const willExpand = kitToggle.getAttribute('aria-expanded') !== 'true';
        kitToggle.setAttribute('aria-expanded', String(willExpand));
        const details = document.getElementById(kitToggle.getAttribute('aria-controls'));
        if (details) details.classList.toggle('is-open', willExpand);
        const cart = ShoogieGolf.cart;
        cart.expandedKits = cart.expandedKits || new Set();
        const kitId = kitToggle.dataset.kitToggle;
        if (willExpand) cart.expandedKits.add(kitId); else cart.expandedKits.delete(kitId);
        return;
      }

      // Cart drawer component kit quantity update
      if (e.target.matches('[data-cart-update-kit]')) {
        e.preventDefault();
        const kitId = e.target.dataset.cartUpdateKit;
        const quantity = parseInt(e.target.dataset.qty);
        if (kitId && quantity >= 0) {
          ShoogieGolf.cart.updateKitItems(kitId, quantity);
        }
      }

      // Cart drawer remove item
      if (e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        const button = e.target.closest('[data-cart-remove]');
        const key = button.dataset.cartRemove;
        ShoogieGolf.cart.removeItem(key);
      }

      // Cart drawer remove component kit
      if (e.target.closest('[data-cart-remove-kit]')) {
        e.preventDefault();
        const button = e.target.closest('[data-cart-remove-kit]');
        const kitId = button.dataset.cartRemoveKit;
        if (kitId) {
          ShoogieGolf.cart.updateKitItems(kitId, 0);
        }
      }
    });
  });

  // CSS for animations
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }

    /* Loading state for any add-to-cart button while /cart/add.js is in flight */
    @keyframes shoobieCartSpin {
      to { transform: rotate(360deg); }
    }
    .is-cart-loading {
      position: relative;
      color: transparent !important;
      pointer-events: none;
    }
    .is-cart-loading::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2.5px solid rgba(255, 255, 255, 0.3);
      border-top-color: #FFFFFF;
      border-radius: 50%;
      animation: shoobieCartSpin 0.7s linear infinite;
    }
    /* For light-background buttons, spinner uses navy instead of white */
    .is-cart-loading.btn-light::after,
    .section--variant-light .is-cart-loading::after {
      border-color: rgba(10, 24, 87, 0.2);
      border-top-color: #0A1857;
    }
    /* Dark variant's Add to Cart is NEON — white spinner washes out there,
       so it flips to navy as well */
    .section--variant-dark .is-cart-loading::after {
      border-color: rgba(10, 24, 87, 0.25);
      border-top-color: #0A1857;
    }

    .cart-item--kit {
      align-items: start;
    }

    .cart-drawer .cart-item.cart-item--kit {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0.9rem;
    }

    .cart-drawer .cart-item.cart-item--kit .cart-item__header {
      align-items: flex-start;
    }

    /* Right-side stack: delete button on top, price below (both right-aligned).
       Replaces the old bottom qty/remove row — bundles are discrete looks, so
       there's no quantity stepper. */
    .cart-item--kit .cart-item__headside {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.6rem;
      flex-shrink: 0;
    }

    .cart-drawer .cart-item.cart-item--kit .cart-item__bottom {
      margin-top: 0.15rem;
      padding-top: 0.85rem;
      border-top: 1px solid rgba(255, 255, 255, 0.16);
    }

    .cart-drawer .cart-item.cart-item--kit .cart-item__actions {
      margin-top: 0;
    }

    .cart-item--kit .cart-kit-groups {
      display: grid;
      gap: 0;
      margin-top: 0.55rem;
      padding-top: 0.85rem;
      border-top: 1px solid rgba(255, 255, 255, 0.16);
    }

    .cart-item--kit .cart-kit-groups__intro {
      margin: 0;
      color: var(--off-white, #F9F8F4);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body);
      font-weight: 800;
      line-height: 1.35;
    }

    .cart-item--kit .cart-kit-group {
      display: grid;
      gap: 0.55rem;
      padding: 0.8rem 0 0.8rem 0.85rem;
    }

    .cart-item--kit .cart-kit-group + .cart-kit-group {
      border-top: 1px solid rgba(255, 255, 255, 0.09);
    }

    .cart-item--kit .cart-kit-group__header {
      display: grid;
      gap: 0.2rem;
    }

    .cart-item--kit .cart-kit-group h4 {
      margin: 0;
      color: rgba(215, 255, 1, 0.85);
      font-family: var(--font-body-family);
      font-size: var(--font-size-caption);
      font-weight: 700;
      letter-spacing: 0.16em;
      line-height: 1;
      text-transform: uppercase;
    }

    .cart-item--kit .cart-kit-group p {
      margin: 0;
      color: rgba(249, 248, 244, 0.72);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body);
      font-weight: 600;
      line-height: 1.35;
    }

    .cart-item--kit .cart-kit-group ul {
      display: grid;
      gap: 0.5rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .cart-item--kit .cart-kit-group li {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;
      gap: 0.7rem;
      color: var(--off-white, #F9F8F4);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body);
      line-height: 1.35;
    }

    .cart-item--kit .cart-kit-line__thumb {
      width: 46px;
      height: 46px;
      border-radius: 7px;
      object-fit: cover;
      background: rgba(255, 255, 255, 0.08);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
      flex-shrink: 0;
    }

    .cart-item--kit .cart-kit-line__text {
      display: grid;
      gap: 0.15rem;
      min-width: 0;
    }

    .cart-item--kit .cart-kit-line__title {
      color: var(--off-white, #F9F8F4);
      font-weight: 800;
      overflow-wrap: anywhere;
    }

    .cart-item--kit .cart-kit-line__meta {
      color: rgba(249, 248, 244, 0.68);
      font-size: var(--font-size-body-sm);
      font-weight: 600;
      line-height: 1.35;
    }

    .cart-item--kit .cart-kit-group strong {
      flex: 0 0 auto;
      color: rgba(249, 248, 244, 0.92);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body);
      font-weight: 700;
      line-height: 1.2;
      text-align: right;
      white-space: nowrap;
    }

    /* Plain-English "what's inside" summary under the bundle title */
    .cart-item--kit .cart-item__contents {
      margin: 0.25rem 0 0;
      color: rgba(249, 248, 244, 0.74);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body-sm);
      font-weight: 600;
      line-height: 1.35;
    }

    /* Collapse/expand toggle for the itemized breakdown */
    .cart-item--kit .cart-kit-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      width: 100%;
      margin-top: 0.4rem;
      padding: 0.75rem 0 0;
      border: none;
      border-top: 1px solid rgba(255, 255, 255, 0.16);
      background: none;
      color: rgba(215, 255, 1, 0.9);
      font-family: var(--font-body-family);
      font-size: var(--font-size-body-sm);
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
    }

    .cart-item--kit .cart-kit-toggle:hover {
      color: var(--neon, #D7FF01);
    }

    .cart-item--kit .cart-kit-toggle__chev {
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    .cart-item--kit .cart-kit-toggle[aria-expanded="true"] .cart-kit-toggle__chev {
      transform: rotate(180deg);
    }

    /* Smooth expand/collapse via grid-template-rows (0fr -> 1fr). The inner
       groups clip with overflow:hidden so the row can fully collapse; visibility
       flips at the end of the collapse so the content also leaves the tab order
       and AT tree when closed. */
    .cart-item--kit .cart-kit-details {
      display: grid;
      grid-template-rows: 0fr;
      visibility: hidden;
      transition: grid-template-rows 0.3s ease, visibility 0.3s;
    }

    .cart-item--kit .cart-kit-details.is-open {
      grid-template-rows: 1fr;
      visibility: visible;
    }

    /* Groups already have a top divider; inside the details it's redundant with
       the toggle's border, so drop it here. overflow/min-height let the row
       collapse fully. */
    .cart-item--kit .cart-kit-details .cart-kit-groups {
      overflow: hidden;
      min-height: 0;
      margin-top: 0;
      padding-top: 0.5rem;
      border-top: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .cart-item--kit .cart-kit-details {
        transition: none;
      }
    }

    .cart-item--kit .cart-item__actions--remove-only {
      justify-content: flex-end;
    }

    @media (max-width: 640px) {
      .cart-item--kit .cart-kit-groups {
        grid-column: 1 / -1;
        padding: 0.8rem;
      }

      .cart-item--kit .cart-kit-group {
        padding: 0.7rem;
      }
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-20px);
      }
    }

    [data-animate] {
      opacity: 0;
      transform: translateY(30px);
      transition: all 0.6s ease;
    }

    [data-animate].animate-in {
      opacity: 1;
      transform: translateY(0);
    }

    .menu-open {
      overflow: hidden;
    }
  `;
  document.head.appendChild(style);

})();
